import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BarChart3, TrendingUp, Users, Activity, Shield, Zap } from 'lucide-react';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: Activity,
      title: t('dashboard.features.realtime'),
      description: 'Dados atualizados a cada segundo',
      color: 'primary',
    },
    {
      icon: BarChart3,
      title: t('dashboard.features.sentiment'),
      description: 'Análise automática de sentimentos',
      color: 'secondary',
    },
    {
      icon: TrendingUp,
      title: t('dashboard.features.keywords'),
      description: 'Palavras-chave mais utilizadas',
      color: 'accent',
    },
    {
      icon: Users,
      title: t('dashboard.features.categories'),
      description: 'Categorização inteligente',
      color: 'primary',
    },
    {
      icon: Shield,
      title: t('dashboard.features.crisis'),
      description: 'Gestão de crise em tempo real',
      color: 'secondary',
    },
    {
      icon: Zap,
      title: t('dashboard.features.multiCompany'),
      description: 'Acesso multiempresa',
      color: 'accent',
    },
  ];

  const sentimentData = [
    { type: 'positive', value: 65, color: 'bg-green-500' },
    { type: 'negative', value: 15, color: 'bg-red-500' },
    { type: 'neutral', value: 12, color: 'bg-gray-500' },
    { type: 'urgent', value: 5, color: 'bg-orange-500' },
    { type: 'suggestion', value: 3, color: 'bg-blue-500' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="dashboard" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('dashboard.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('dashboard.subtitle')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
        >
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            const colorClasses = {
              primary: 'bg-primary-100 text-primary-600',
              secondary: 'bg-secondary-100 text-secondary-600',
              accent: 'bg-accent-100 text-accent-600',
            };

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className={`w-12 h-12 rounded-lg ${colorClasses[feature.color as keyof typeof colorClasses]} flex items-center justify-center mb-4`}>
                  <IconComponent size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Dashboard Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-gray-900 rounded-2xl p-8 shadow-2xl"
        >
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold text-white">Dashboard VoizX</h3>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-400 text-sm">Online</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Sentiment Analysis Chart */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Análise de Sentimentos</h4>
              <div className="space-y-3">
                {sentimentData.map((item, index) => (
                  <motion.div
                    key={item.type}
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${item.value}%` } : {}}
                    transition={{ duration: 1, delay: 0.6 + index * 0.1 }}
                    className="relative"
                  >
                    <div className="flex justify-between text-sm text-gray-300 mb-1">
                      <span>{t(`dashboard.sentimentTypes.${item.type}`)}</span>
                      <span>{item.value}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <motion.div
                        className={`h-2 rounded-full ${item.color}`}
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Real-time Metrics */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-white mb-4">Métricas em Tempo Real</h4>
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold text-primary-400">1,247</div>
                  <div className="text-sm text-gray-400">Interações Hoje</div>
                </motion.div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.9 }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold text-secondary-400">98.5%</div>
                  <div className="text-sm text-gray-400">Taxa de Sucesso</div>
                </motion.div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 1.0 }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold text-accent-400">2.3s</div>
                  <div className="text-sm text-gray-400">Tempo Médio</div>
                </motion.div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: 1.1 }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold text-green-400">24/7</div>
                  <div className="text-sm text-gray-400">Disponibilidade</div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Keywords Cloud */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-8 bg-gray-800 rounded-xl p-6"
          >
            <h4 className="text-lg font-semibold text-white mb-4">Palavras-chave Mais Faladas</h4>
            <div className="flex flex-wrap gap-2">
              {['Informação', 'Horário', 'Localização', 'Preço', 'Serviço', 'Ajuda', 'Contato', 'Emergência'].map((keyword, index) => (
                <motion.span
                  key={keyword}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.3, delay: 1.4 + index * 0.1 }}
                  className="px-3 py-1 bg-primary-600 text-white text-sm rounded-full"
                >
                  {keyword}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Dashboard; 